import {
  Checkbox,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { FormikValues, useFormikContext } from 'formik';
import { important } from '../../../../utils/global/helper';
import InputWrapper from '../InputWrapper';

interface MultiSelectProps {
  name: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  options: {
    value: string;
    label: string;
  }[];
  tooltip?: string;
  allText?: string;
  autoWidth?: boolean;
  required?: boolean;
}

function MultiSelect({
  name,
  label,
  placeholder,
  disabled,
  options,
  tooltip,
  allText,
  autoWidth = true,
  required,
}: MultiSelectProps) {
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  const selectedList = ((values as FormikValues)[name] ?? []) as string[];

  const isAllSelected = options.every(option =>
    selectedList.includes(option.value),
  );

  const renderValue = (selected: string[]) => {
    if (!selected.length) {
      return placeholder || `No ${name}`;
    }
    if (isAllSelected) {
      return allText || `All ${name}`;
    }
    const selectedLabels = selected.map(
      id => options.find(option => option.value === id)?.label,
    );
    return selectedLabels.join(', ');
  };

  return (
    <InputWrapper
      label={label}
      name={name}
      tooltip={tooltip}
      required={required}>
      <Select
        multiple
        renderValue={selected => renderValue(selected as unknown as string[])}
        sx={theme => ({
          height: important('44px'),
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '0.5px',
            borderColor: theme.palette.lightOutlineGrey.main,
          },
        })}
        value={(values as any)?.[name]}
        onChange={async (event: SelectChangeEvent) => {
          const { value } = event.target;
          const newList = value as unknown as string[];
          if (newList.includes('all') && !isAllSelected) {
            setFieldValue?.(
              name,
              options.map(option => option.value),
            );
            return;
          }
          if (newList.includes('all') && isAllSelected) {
            setFieldValue?.(name, []);
            return;
          }
          setFieldValue?.(name, newList);
        }}
        input={<OutlinedInput />}
        autoWidth={autoWidth}
        displayEmpty
        label={placeholder}
        disabled={disabled}
        onBlur={() => {
          setFieldTouched?.(name, true);
        }}>
        <MenuItem value="all" sx={{ gap: 1 }}>
          <Checkbox sx={{ padding: 0.5 }} checked={isAllSelected} />
          <ListItemText
            primaryTypographyProps={{
              fontSize: '0.875rem',
            }}
            primary={allText || `All ${name}`}
          />
        </MenuItem>
        {options.map(option => (
          <MenuItem key={option.value} value={option.value} sx={{ gap: 1 }}>
            <Checkbox
              checked={selectedList.includes(option.value)}
              sx={{
                padding: 0.5,
              }}
            />
            <ListItemText
              primaryTypographyProps={{
                fontSize: '0.875rem',
              }}
              primary={option.label}
            />
          </MenuItem>
        ))}
      </Select>
    </InputWrapper>
  );
}

export default MultiSelect;
